import _ from "lodash";
import useDealBuilderAPI from "./useDealBuilderAPI.ts";
import useRealTimeAAA from "./useRealTimeAAA.ts";
import useEODAAA from "./useEODAAA.ts";
import useUsTreasury from "./useUsTreasury.ts";
import useSimilarDealsAPI from "./useSimilarDealsAPI.ts";
import useDatabaseAPI from "../../pricing-management/hooks/useDatabaseAPI.ts";
import useDealBuilderInputsStore from "../../stores/deal-builder-inputs.store.ts";
import useDealBuilderExtractionStore from "../../stores/deal-builder-extraction.store.ts";
import useDealBuilderAlertsStore from "../../stores/deal-builder-alerts.store.ts";
import useDealBuilderResultStore from "../../stores/deal-builder-result.store.ts";
import { validateDealBuilderInputs } from "../utils/deal-builder-inputs.utils.ts";
import { DealBuilder } from "../services/deal-builder.ts";
import { SourcesAndUsesUtils } from "../services/utils/sources-and-uses.utils.ts";
import { DebtServiceUtils } from "../services/utils/debt-service.utils.ts";
import { TaxDesignation } from "../services/utils/primary-constants.ts";

/**
 * Builds the deal from whatever is currently in the input store, using the
 * staged bond schedule when one was imported.
 *
 * This lives in a hook rather than in the buttons panel because other pages
 * prefill the stores and then build straight away, without the user pressing
 * "Build Deal" (see useLoadIssueIntoDealBuilder).
 */
export default function useBuildDeal() {
    const { setDealBuilderService, setResetCopy } = useDealBuilderResultStore();
    const { setSnackMessage } = useDealBuilderAlertsStore();
    const { updateSimilarDeals } = useSimilarDealsAPI();
    const { saveDeal } = useDatabaseAPI();

    const { getAlignedRealTimeAAA } = useRealTimeAAA();
    const { getAlignedAAA } = useEODAAA();
    const { getAlignedUSTreasury } = useUsTreasury();
    const { fetchCouponsAndSpreads } = useDealBuilderAPI();

    async function buildDeal() {
        // Read the stores directly so a freshly imported schedule is used even
        // when this runs right after the import, before a re-render.
        const inputStore = useDealBuilderInputsStore.getState();
        const { extractedBonds, termRanges } = useDealBuilderExtractionStore.getState();

        const inputValidation = validateDealBuilderInputs(inputStore);
        if (!inputValidation.valid) return setSnackMessage(inputValidation.error);

        const dealBuilder = new DealBuilder({
            bondInsurance: inputStore.bondInsurance,
            borrower: inputStore.borrower,
            callDate: inputStore.useCallDate ? inputStore.callDate : undefined,
            capitalizedInterest: 0,
            deliveryDate: inputStore.deliveryDate,
            denomination: inputStore.denomination,
            firstCouponDate: inputStore.firstCouponDate,
            bonds: extractedBonds.length > 0 ?
                _.cloneDeep(extractedBonds) :
                DebtServiceUtils.datesToDefaultBonds(
                    inputStore.firstPrincipalDate,
                    inputStore.finalPrincipalDate,
                    inputStore.principalRate,
                ),
            issuanceTitle: inputStore.issuanceTitle,
            marketConditionsDate: inputStore.marketConditionsDate,
            ratingAssumption: inputStore.ratingAssumption,
            saleMethod: inputStore.saleMethod,
            securityType: inputStore.securityType,
            stateEnhancement: inputStore.useStateEnhancement ? inputStore.stateEnhancement : undefined,
            stateOfIssuance: inputStore.stateOfIssuance,
            taxDesignation: inputStore.taxDesignation,
            totalTargetParAmount: inputStore.parAmount,
            sourcesAndUses: SourcesAndUsesUtils.getDefaultCategories(),
            cushion: 0,

            fetchCouponsAndSpreads: fetchCouponsAndSpreads,
            getAlignedAAA: getAlignedAAA,
            getAlignedRealTimeAAA: getAlignedRealTimeAAA,
            getAlignedUSTreasury: getAlignedUSTreasury,
            saveDeal: saveDeal,

            updatePrincipals: true
        });

        // Rebuild the term bonds found in the imported schedule from their
        // serial parts.
        if (extractedBonds.length > 0 && termRanges.length > 0) {
            for (const [start, end] of termRanges) {
                dealBuilder.groupSerialBonds(start.toISOString(), end.toISOString());
            }
            dealBuilder.updateDebtService(false);
        }

        setDealBuilderService(dealBuilder);

        await Promise.all([
            (async () => {
                const productionResult = await dealBuilder.updateProduction(dealBuilder.bonds);
                if (!productionResult.valid) setSnackMessage(productionResult.error);
                if (dealBuilder.taxDesignation === TaxDesignation.Taxable) {
                    await dealBuilder.updateYieldCurve("ust");
                } else if (extractedBonds.length > 0) {
                    // Imported deals also load the yield curve, exactly like
                    // pressing Curve > Set EOD AAA on the production page.
                    await dealBuilder.updateYieldCurve("eod_aaa");
                }

                setDealBuilderService(dealBuilder);
                setResetCopy(_.cloneDeep(dealBuilder));
            })(),
            (async () => {
                updateSimilarDeals(dealBuilder);
            })(),
        ])
    }

    return { buildDeal };
}
